import { h } from 'preact'
import clsx from 'clsx'

interface BadgeData {
  title: string
  text_colour: string
  background_colour?: string
  image?: string
  off_messaging?: string[]
}

interface BadgeProps {
  badge: BadgeData
  badgeLocation?: string
  product?: any
}

const TextBadge = ({ badge, badgeLocation, product }: { badge: BadgeData; badgeLocation?: string; product?: any }) => {
  let showOffMessaging = false
  let salePercentage = 0

  if (product && product.variants && product.variants[0]) {
    const variant = product.variants[0]
    const offMessagingString = badge.off_messaging?.join(',') || ''

    if (variant.compare_at_price && variant.compare_at_price !== variant.price) {
      if (badgeLocation === 'PLP' && offMessagingString.includes('PLP')) {
        showOffMessaging = true
      } else if (badgeLocation === 'PDP' && offMessagingString.includes('PDP')) {
        showOffMessaging = true
      }

      if (showOffMessaging) {
        salePercentage = Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
      }
    }
  }

  // Mobile cards stack a two-word badge over two lines; the flex row at `lg` restores the
  // single-line label. Any other word count keeps one line at desktop and wraps balanced on
  // mobile rather than overflowing the card. Off-messaging appends ` | -X%`, which would
  // read oddly split across lines, so it opts out of stacking.
  const words = badge.title?.trim().split(/\s+/) ?? []
  const stackWords = words.length === 2 && !showOffMessaging

  return (
    <div
      className={clsx('shrink-0 flex size-fit text-[--badge-text-colour]', {
        'bg-[--badge-bg-colour] py-0.25': badge.background_colour
      })}
      style={{
        '--badge-text-colour': badge.text_colour,
        '--badge-bg-colour': 'transparent'
      } as React.CSSProperties}
      // Not sure if I'll need this yet so I'm leaving it in for now
      data-background-colour={badge.background_colour}
    >
      {stackWords ? (
        <span className="btn-label flex flex-col items-start whitespace-nowrap lg:flex-row lg:gap-x-[0.25em]">
          <span>{words[0]}</span>
          <span>{words[1]}</span>
        </span>
      ) : (
        <span className="btn-label whitespace-nowrap lg-max:whitespace-normal lg-max:text-balance">
          {badge.title}
          {showOffMessaging && ` | -${salePercentage}%`}
        </span>
      )}
    </div>
  )
}

const ImageBadge = ({ badge }: { badge: BadgeData }) => (
  <div className="flex size-fit max-w-[75px] children:size-auto children:object-contain">
    <img src={badge.image} alt={badge.title || 'Badge'} />
  </div>
)

export const Badge = ({ badge, badgeLocation, product }: BadgeProps) => {
  if (!badge) return null

  return badge.image
    ? <ImageBadge badge={badge} />
    : <TextBadge badge={badge} badgeLocation={badgeLocation} product={product} />
}
